import { Alert, Button, Form, Input, Spin, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import apiClient from "../../Utils/apiClient"
import { useDispatch } from "react-redux"
import { setUser } from "../../Redux/Slices/UserSlice"
import AuthShell from "../../Layout/AuthShell"

const { Paragraph, Text } = Typography

const TwoFactorSetup = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [qrCode, setQrCode] = useState("")
  const [secret, setSecret] = useState("")
  const [error, setError] = useState("")
  const dispatch = useDispatch()

  useEffect(() => {
    const fetchSetupData = async () => {
      try {
        const res = await apiClient("auth/2fa/setup", "GET", null, {
          refresh: false,
        })
        if (res.qrCode) {
          setQrCode(res.qrCode)
          setSecret(res.secret)
        } else {
          setError(res.message || "Could not start two-factor setup. Sign in again.")
        }
      } catch (err) {
        setError("Could not load the authenticator QR code.")
      } finally {
        setLoading(false)
      }
    }
    fetchSetupData()
  }, [])

  const onFinish = async (values) => {
    try {
      setSubmitting(true)
      setError("")
      const token = Array.isArray(values.token)
        ? values.token.join("")
        : String(values.token ?? "").replace(/\s+/g, "")
      const res = await apiClient(
        "auth/2fa/login",
        "POST",
        { token },
        { refresh: false }
      )
      if (res.success) {
        localStorage.setItem("user", JSON.stringify(res))
        dispatch(setUser(res.data?.user))
        navigate("/")
      } else {
        setError(res.message || "That code is incorrect. Wait for a new one and try again.")
      }
    } catch (err) {
      setError("Could not verify the code. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && !qrCode) {
    return (
      <AuthShell
        title="Setting up two-factor authentication"
        steps={["Credentials", "Verify", "Console"]}
        activeStep={2}
        badge="First-time setup"
      >
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Set up two-factor authentication"
      subtitle="This is required once for every administrator account."
      footer="Keep the recovery key in a password manager. You will need it if you lose the authenticator app."
      steps={["Credentials", "Verify", "Console"]}
      activeStep={2}
      badge="First-time setup"
    >
      {error && !qrCode ? (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message={error}
          action={
            <Button size="small" onClick={() => navigate("/login")}>
              Back to sign-in
            </Button>
          }
        />
      ) : null}

      <div className="auth-steps">
        <div className="auth-step">
          <span className="auth-step__index">1</span>
          <span>Open Google Authenticator, Authy, or Microsoft Authenticator.</span>
        </div>
        <div className="auth-step">
          <span className="auth-step__index">2</span>
          <span>Scan the QR code, or enter the key manually if you cannot scan.</span>
        </div>
        <div className="auth-step">
          <span className="auth-step__index">3</span>
          <span>Enter the 6-digit code to confirm this device.</span>
        </div>
      </div>

      {qrCode ? (
        <div className="auth-2fa-setup">
          <img src={qrCode} alt="Two-factor authentication QR code" />
          <Paragraph copyable={{ text: secret }} className="auth-2fa-secret">
            <Text type="secondary">Can’t scan? Use this key</Text>
            <br />
            <Text code>{secret}</Text>
          </Paragraph>
        </div>
      ) : null}

      {error && qrCode ? (
        <Alert
          type="error"
          showIcon
          message={error}
          className="mb-3"
          closable
          onClose={() => setError("")}
        />
      ) : null}

      <Form
        className="auth-form"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item
          label="Authenticator code"
          name="token"
          rules={[
            { required: true, message: "Enter the 6-digit code" },
            { len: 6, message: "Enter all 6 digits" },
          ]}
        >
          <Input.OTP length={6} size="large" inputMode="numeric" />
        </Form.Item>
        <Form.Item className="mb-0">
          <Button
            className="auth-submit"
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={submitting}
            disabled={!qrCode}
          >
            Verify and enable 2FA
          </Button>
        </Form.Item>
      </Form>
    </AuthShell>
  )
}

export default TwoFactorSetup
